/**
 * Q&A Dialog Component
 *
 * Create/edit Q&A items with question pattern, answer, tags, and profile binding.
 * Per design.md: Premium UI, semantic tokens, lucide icons.
 */

import { useState, useEffect } from 'react';
import { useAction, useQuery } from 'convex/react';
import { api } from 'convex/_generated/api';
import type { Doc, Id } from 'convex/_generated/dataModel';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
    Button, Input, Skeleton,
    Card, CardContent,
    AlertTriangle,
} from '@uprosal/ui';
import { Textarea } from '@uprosal/ui';

// Type for Q&A item without embedding
type QAItem = Omit<Doc<'questionAnswers'>, 'embedding'>;
type Profile = Doc<'connectedProfiles'>;

// Duplicate match type from Convex action
interface DuplicateMatch {
    _id: Id<'questionAnswers'>;
    questionPattern: string;
    answer: string;
    tags: string[];
    usageCount: number;
    confidence: number;
}

interface QADialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    qa: QAItem | null;
}

export function QADialog({ open, onOpenChange, qa }: QADialogProps) {
    const [questionPattern, setQuestionPattern] = useState('');
    const [answer, setAnswer] = useState('');
    const [tagsInput, setTagsInput] = useState('');
    const [profileId, setProfileId] = useState<Id<'connectedProfiles'> | undefined>(undefined);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');

    // Duplicate detection state
    const [isCheckingDuplicate, setIsCheckingDuplicate] = useState(false);
    const [duplicateMatch, setDuplicateMatch] = useState<DuplicateMatch | null>(null);

    const profiles = useQuery(api.queries.profiles.listConnected, {}) as Profile[] | undefined;
    const createQA = useAction(api.actions.questionAnswers.create);
    const updateQA = useAction(api.actions.questionAnswers.update);
    const checkForDuplicate = useAction(api.actions.questionAnswers.checkForDuplicate);

    // Initialize form when editing
    useEffect(() => {
        if (qa) {
            setQuestionPattern(qa.questionPattern);
            setAnswer(qa.answer);
            setTagsInput(qa.tags.join(', '));
            setProfileId(qa.profileId);
        } else {
            setQuestionPattern('');
            setAnswer('');
            setTagsInput('');
            setProfileId(undefined);
        }
        setError('');
        setDuplicateMatch(null);
    }, [qa, open]);

    const handleSubmit = async (forceCreate = false) => {
        if (!questionPattern.trim()) {
            setError('Question is required');
            return;
        }
        if (!answer.trim()) {
            setError('Answer is required');
            return;
        }

        setError('');

        const tags = tagsInput
            .split(',')
            .map(t => t.trim())
            .filter(t => t.length > 0);

        // When creating new (not editing) and not forcing create, check for duplicates
        if (!qa && !forceCreate && !duplicateMatch) {
            setIsCheckingDuplicate(true);
            try {
                const result = await checkForDuplicate({
                    questionPattern: questionPattern.trim(),
                    answer: answer.trim(),
                    profileId,
                });

                if (result.hasDuplicate && result.match) {
                    setDuplicateMatch(result.match);
                    setIsCheckingDuplicate(false);
                    return; // Show duplicate dialog instead of saving
                }
            } catch (err) {
                console.error('Failed to check for duplicate:', err);
                // Continue with save if duplicate check fails
            } finally {
                setIsCheckingDuplicate(false);
            }
        }

        setIsSubmitting(true);

        try {
            if (qa) {
                await updateQA({
                    qaId: qa._id,
                    questionPattern: questionPattern.trim(),
                    answer: answer.trim(),
                    tags,
                    profileId,
                });
            } else {
                await createQA({
                    questionPattern: questionPattern.trim(),
                    answer: answer.trim(),
                    tags,
                    profileId,
                });
            }
            onOpenChange(false);
        } catch (err) {
            console.error('Failed to save Q&A item:', err);
            setError('Failed to save. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    // Handle "Update Existing" when duplicate is found
    const handleUpdateExisting = async () => {
        if (!duplicateMatch) return;

        setIsSubmitting(true);
        setError('');

        const tags = tagsInput
            .split(',')
            .map(t => t.trim())
            .filter(t => t.length > 0);

        try {
            await updateQA({
                qaId: duplicateMatch._id,
                questionPattern: questionPattern.trim(),
                answer: answer.trim(),
                tags,
                profileId,
            });
            onOpenChange(false);
        } catch (err) {
            console.error('Failed to update existing Q&A item:', err);
            setError('Failed to update. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    // Handle "Save as New" - force create despite duplicate
    const handleSaveAsNew = () => {
        setDuplicateMatch(null);
        handleSubmit(true);
    };

    // Handle cancel duplicate dialog
    const handleCancelDuplicate = () => {
        setDuplicateMatch(null);
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="w-[600px] max-w-[90vw] max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>
                        {qa ? 'Edit Q&A' : 'Add Q&A'}
                    </DialogTitle>
                </DialogHeader>

                <div className="space-y-4 py-4">
                    {/* Question Pattern */}
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-foreground">
                            Question *
                        </label>
                        <Textarea
                            placeholder="e.g., What is your availability for this project?"
                            value={questionPattern}
                            onChange={(e) => setQuestionPattern(e.target.value)}
                            rows={2}
                        />
                        <p className="text-xs text-muted-foreground">
                            Enter the screening question or a pattern to match similar questions
                        </p>
                    </div>

                    {/* Answer */}
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-foreground">
                            Answer *
                        </label>
                        <Textarea
                            placeholder="Your prepared answer to this question..."
                            value={answer}
                            onChange={(e) => setAnswer(e.target.value)}
                            rows={6}
                        />
                    </div>

                    {/* Tags */}
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-foreground">
                            Tags
                        </label>
                        <Input
                            placeholder="availability, rate, experience (comma separated)"
                            value={tagsInput}
                            onChange={(e) => setTagsInput(e.target.value)}
                        />
                        <p className="text-xs text-muted-foreground">
                            Tags help organize and filter your Q&A library
                        </p>
                    </div>

                    {/* Profile binding */}
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-foreground">
                            Bind to Profile (optional)
                        </label>
                        {profiles === undefined ? (
                            <Skeleton className="h-10 w-full" />
                        ) : (
                            <select
                                value={profileId || ''}
                                onChange={(e) => setProfileId(e.target.value as Id<'connectedProfiles'> || undefined)}
                                className="w-full px-3 py-2 rounded-md border border-input bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                            >
                                <option value="">All Profiles</option>
                                {profiles.map((profile) => (
                                    <option key={profile._id} value={profile._id}>
                                        {profile.profileLabel}
                                    </option>
                                ))}
                            </select>
                        )}
                        <p className="text-xs text-muted-foreground">
                            Bind to a profile to show this answer only when using that profile
                        </p>
                    </div>

                    {/* Duplicate Warning */}
                    {duplicateMatch && (
                        <Card className="border-amber-500 bg-amber-50 dark:bg-amber-950/30">
                            <CardContent className="pt-4">
                                <div className="flex items-start gap-3">
                                    <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                                    <div className="flex-1 space-y-3">
                                        <div>
                                            <p className="font-medium text-amber-900 dark:text-amber-100">
                                                Similar Q&A Found ({duplicateMatch.confidence}% match)
                                            </p>
                                            <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                                                A similar question already exists. Would you like to update it or save as new?
                                            </p>
                                        </div>
                                        <div className="p-3 bg-white dark:bg-background rounded-md border border-amber-200 dark:border-amber-800">
                                            <p className="text-sm font-medium text-foreground mb-1">
                                                Existing Question:
                                            </p>
                                            <p className="text-sm text-muted-foreground line-clamp-2">
                                                {duplicateMatch.questionPattern}
                                            </p>
                                            <p className="text-xs text-muted-foreground mt-2">
                                                Used {duplicateMatch.usageCount} times
                                            </p>
                                        </div>
                                        <div className="flex gap-2 flex-wrap">
                                            <Button
                                                size="sm"
                                                onClick={handleUpdateExisting}
                                                disabled={isSubmitting}
                                            >
                                                {isSubmitting ? 'Updating...' : 'Update Existing'}
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={handleSaveAsNew}
                                                disabled={isSubmitting}
                                            >
                                                Save as New
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant="ghost"
                                                onClick={handleCancelDuplicate}
                                                disabled={isSubmitting}
                                            >
                                                Cancel
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    )}

                    {/* Error message */}
                    {error && (
                        <p className="text-sm text-destructive">{error}</p>
                    )}
                </div>

                <DialogFooter className="mt-6 gap-3">
                    <Button
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        disabled={isSubmitting || isCheckingDuplicate}
                    >
                        Cancel
                    </Button>
                    {!duplicateMatch && (
                        <Button
                            onClick={() => handleSubmit()}
                            disabled={isSubmitting || isCheckingDuplicate}
                        >
                            {isCheckingDuplicate ? 'Checking...' : isSubmitting ? 'Saving...' : (qa ? 'Save Changes' : 'Add Q&A')}
                        </Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
